import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	Loader2,
	ChevronDown,
	Check,
	User as UserIcon,
	AlertTriangle,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";
import RecorrenciaFields from "../RecorrenciaFields";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";

const ScheduledMessageModal = ({
	open,
	onClose,
	scheduledMessageId,
	onSaved,
	// Preenchida quando o agendamento nasce de um clique num dia do
	// calendário: "yyyy-MM-ddTHH:mm", o mesmo formato do input.
	dataInicial,
}) => {
	const [contactPopoverOpen, setContactPopoverOpen] = useState(false);
	const [contactOptions, setContactOptions] = useState([]);
	const [searchParam, setSearchParam] = useState("");
	const [searching, setSearching] = useState(false);
	const [selectedContact, setSelectedContact] = useState(null);
	const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
	const [body, setBody] = useState("");
	const [scheduledFor, setScheduledFor] = useState("");
	const [recurrence, setRecurrence] = useState(null);
	const [sendIfMissed, setSendIfMissed] = useState(false);
	const [saving, setSaving] = useState(false);
	// Se os campos vieram do agendamento anterior — só para avisar na tela.
	const [veioDoAnterior, setVeioDoAnterior] = useState(false);
	const searchInputRef = useRef(null);

	const { whatsApps, loading: loadingWhatsapps } = useWhatsApps();

	// Um resumo em uma frase: quem recebe, por qual número, quando. Sem isso a
	// pessoa só descobre o que combinou depois que a mensagem já saiu.
	const conexaoEscolhida = whatsApps.find(
		(w) => String(w.id) === String(selectedWhatsappId)
	);
	const quandoLegivel = scheduledFor
		? `${scheduledFor.slice(8, 10)}/${scheduledFor.slice(
				5,
				7
		  )}/${scheduledFor.slice(0, 4)} às ${scheduledFor.slice(11, 16)}`
		: null;
	const jaPassou = scheduledFor
		? new Date(scheduledFor).getTime() < Date.now()
		: false;

	useEffect(() => {
		(async () => {
			if (!scheduledMessageId || !open) return;
			try {
				const { data } = await api.get(
					`/scheduled-messages/${scheduledMessageId}`
				);
				setSelectedContact(data.contact || null);
				setSelectedWhatsappId(data.whatsappId ? String(data.whatsappId) : "");
				setBody(data.body || "");
				setRecurrence(data.recurrence || null);
				setSendIfMissed(Boolean(data.sendIfMissed));
				if (data.scheduledFor) {
					setScheduledFor(
						format(parseISO(data.scheduledFor), "yyyy-MM-dd'T'HH:mm")
					);
				}
			} catch (err) {
				toastError(err);
			}
		})();
	}, [scheduledMessageId, open]);

	// Agendamento NOVO: começa com o que foi usado da última vez.
	//
	// Quem agenda o mesmo aviso toda semana reescrevia o texto e reconfigurava
	// a recorrência a cada vez. Destinatário e data ficam de fora de propósito
	// — são justamente o que muda, e sugeri-los seria convidar a mandar para a
	// pessoa errada na hora errada.
	useEffect(() => {
		if (!open || scheduledMessageId) return;

		let ativo = true;

		(async () => {
			try {
				const { data } = await api.get("/scheduled-messages/ultimo-modelo");

				if (!ativo || !data?.modelo) return;

				// Não sobrescreve o que a pessoa já começou a digitar.
				setBody((atual) => atual || data.modelo.body || "");
				setRecurrence((atual) => atual || data.modelo.recurrence || null);
				setSendIfMissed((atual) => atual || Boolean(data.modelo.sendIfMissed));
				setSelectedWhatsappId((atual) =>
					atual || (data.modelo.whatsappId ? String(data.modelo.whatsappId) : ""),
				);
				setVeioDoAnterior(true);
			} catch {
				// Sem sugestão a tela segue igual — campos vazios, como antes.
			}
		})();

		return () => {
			ativo = false;
		};
	}, [open, scheduledMessageId]);

	// Só vale para agendamento novo: numa edição a data já veio do servidor.
	useEffect(() => {
		if (open && !scheduledMessageId && dataInicial) {
			setScheduledFor(dataInicial);
		}
	}, [open, scheduledMessageId, dataInicial]);

	useEffect(() => {
		if (!open || searchParam.length < 2) {
			setSearching(false);
			return;
		}
		setSearching(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("/contacts", {
						params: { searchParam },
					});
					setContactOptions(data.contacts || []);
					setSearching(false);
				} catch (err) {
					setSearching(false);
					toastError(err);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	const handleClose = () => {
		onClose();
		setSelectedContact(null);
		setSelectedWhatsappId("");
		setBody("");
		setScheduledFor("");
		setRecurrence(null);
		setSendIfMissed(false);
		setSearchParam("");
		setContactOptions([]);
	};

	const handleSelectContact = (contact) => {
		setSelectedContact(contact);
		setContactPopoverOpen(false);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		if (!selectedContact) {
			toast.error("Selecione um contato.");
			return;
		}
		if (!selectedWhatsappId) {
			toast.error("Selecione uma conexão.");
			return;
		}
		if (!body.trim()) {
			toast.error("Digite a mensagem.");
			return;
		}
		if (!scheduledFor) {
			toast.error("Selecione a data e hora.");
			return;
		}

		// Repetição semanal sem nenhum dia marcado é engano de preenchimento, e
		// gravar assim faz o envio cair num dia que ninguém escolheu.
		if (
			recurrence?.tipo === "weekly" &&
			recurrence.diasDaSemana &&
			recurrence.diasDaSemana.length === 0
		) {
			toast.error("Marque ao menos um dia da semana, ou tire a repetição.");
			return;
		}

		if (recurrence?.terminaEm === "") {
			toast.error("Escolha até que data a repetição vai.");
			return;
		}

		const payload = {
			contactId: selectedContact.id,
			whatsappId: +selectedWhatsappId,
			body,
			scheduledFor: new Date(scheduledFor).toISOString(),
			recurrence,
			sendIfMissed,
		};

		setSaving(true);
		try {
			let data;
			if (scheduledMessageId) {
				({ data } = await api.put(
					`/scheduled-messages/${scheduledMessageId}`,
					payload
				));
			} else {
				({ data } = await api.post("/scheduled-messages", payload));
			}
			if (onSaved) {
				onSaved({ ...data, contact: data.contact || selectedContact });
			}
			toast.success("Mensagem agendada com sucesso!");
			handleClose();
		} catch (err) {
			toastError(err);
		}
		setSaving(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{scheduledMessageId
							? "Editar este envio agendado"
							: "Marcar um envio para um contato"}
					</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSave} className="space-y-4">
					<div className="space-y-1.5">
						<Label>Quem vai receber</Label>
						<Popover
							open={contactPopoverOpen}
							onOpenChange={(o) => {
								setContactPopoverOpen(o);
								if (o) setTimeout(() => searchInputRef.current?.focus(), 0);
							}}
						>
							<PopoverTrigger asChild>
								<button
									type="button"
									className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs focus:outline-hidden focus:ring-1 focus:ring-ring"
								>
									<UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
									<span
										className={cn(
											"flex-1 truncate text-left",
											!selectedContact && "text-muted-foreground"
										)}
									>
										{selectedContact
											? selectedContact.name
											: "Buscar contato"}
									</span>
									<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
								</button>
							</PopoverTrigger>
							<PopoverContent
								className="w-(--radix-popover-trigger-width) p-1"
								align="start"
							>
								<Input
									ref={searchInputRef}
									placeholder="Digite para buscar"
									value={searchParam}
									onChange={(e) => setSearchParam(e.target.value)}
									className="mb-1"
								/>
								<div className="max-h-52 overflow-y-auto">
									{searching && (
										<div className="flex items-center justify-center py-3">
											<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
										</div>
									)}
									{!searching &&
										searchParam.length >= 2 &&
										contactOptions.length === 0 && (
											<p className="px-2 py-1.5 text-sm text-muted-foreground">
												Nenhum contato encontrado
											</p>
										)}
									{!searching &&
										contactOptions.map((contact) => (
											<button
												type="button"
												key={contact.id}
												onClick={() => handleSelectContact(contact)}
												className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
											>
												<Check
													className={cn(
														"h-3.5 w-3.5 shrink-0",
														selectedContact?.id === contact.id
															? "opacity-100"
															: "opacity-0"
													)}
												/>
												<span className="flex-1 truncate">{contact.name}</span>
												<span className="text-xs text-muted-foreground">
													{contact.number}
												</span>
											</button>
										))}
								</div>
							</PopoverContent>
						</Popover>
					</div>

					<div className="space-y-1.5">
						<Label>De qual número sai</Label>
						<Select
							value={selectedWhatsappId || undefined}
							onValueChange={(v) => setSelectedWhatsappId(v)}
						>
							<SelectTrigger>
								<SelectValue
									placeholder={
										loadingWhatsapps ? "Carregando..." : "Selecione a conexão"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{whatsApps.map((whatsapp) => (
									<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
										{whatsapp.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							É esse o número que vai aparecer no celular do contato.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="body">O que será enviado</Label>
						<Textarea
							id="body"
							rows={4}
							value={body}
							onChange={(e) => setBody(e.target.value)}
						/>
						{veioDoAnterior && (
							<p className="text-xs leading-relaxed text-muted-foreground">
								Preenchido com o último agendamento feito aqui — texto,
								recorrência e conexão. Altere o que precisar.
							</p>
						)}
						<p className="text-xs text-muted-foreground">
							Escreva como você falaria — o contato recebe exatamente este texto,
							sem nenhum aviso de que é automático.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="scheduledFor">Quando enviar</Label>
						<Input
							id="scheduledFor"
							type="datetime-local"
							value={scheduledFor}
							onChange={(e) => setScheduledFor(e.target.value)}
						/>
						<p className="text-xs text-muted-foreground">
							O sistema confere a cada minuto. Hora que já passou há mais de 15
							minutos só é enviada se você ligar a recuperação abaixo.
						</p>
					</div>

					<RecorrenciaFields
						recurrence={recurrence}
						onRecurrenceChange={setRecurrence}
						sendIfMissed={sendIfMissed}
						onSendIfMissedChange={setSendIfMissed}
					/>

					{/* Confirmação em uma frase, antes de salvar. */}
					<div className="rounded-md bg-muted p-2.5 text-xs leading-relaxed">
						<p className="flex gap-1.5">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							<span>
								{selectedContact && quandoLegivel ? (
									<>
										Vamos enviar essa mensagem para{" "}
										<span className="font-medium">{selectedContact.name}</span>
										{selectedContact.number ? ` (${selectedContact.number})` : ""}{" "}
										em <span className="font-medium">{quandoLegivel}</span>
										{conexaoEscolhida ? `, pela conexão ${conexaoEscolhida.name}` : ""}
										. Ninguém precisa estar com o sistema aberto.
										{jaPassou &&
											(sendIfMissed
												? " Como essa hora já passou, o envio sai em até um minuto."
												: " Essa hora já passou: ligue a recuperação abaixo, senão o envio será pulado.")}
									</>
								) : (
									"Escolha o contato e a data para ver aqui, em uma frase, o que vai acontecer."
								)}
							</span>
						</p>
						<p className="mt-1.5 text-muted-foreground">
							Depois de salvar, dá para impedir o envio clicando em “Cancelar o
							envio” na lista — mas só antes da hora marcada.
						</p>
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={handleClose}
							disabled={saving}
						>
							Cancelar
						</Button>
						<Button type="submit" disabled={saving}>
							{saving && <Loader2 className="h-4 w-4 animate-spin" />}
							{scheduledMessageId ? "Salvar alterações" : "Marcar envio"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default ScheduledMessageModal;
